import hash from 'hash.js';
import * as z from 'zod';

import * as api from '@gitbook/api-client';
import { webConfig } from '@gitbook/env';

export const HostnamePointerOrganizationSchema = z.object({
    type: z.literal('organization'),
    organization: z.string(),
});
export type HostnamePointerOrganization = z.infer<typeof HostnamePointerOrganizationSchema>;

export const HostnamePointerSiteSchema = z.object({
    type: z.literal('site'),
    /** The site's `gid` */
    site: z.string(),
});
export type HostnamePointerSite = z.infer<typeof HostnamePointerSiteSchema>;

export const CustomHostnamePointerSchema = z.discriminatedUnion('type', [
    HostnamePointerOrganizationSchema,
    HostnamePointerSiteSchema,
]);
export type CustomHostnamePointer = z.infer<typeof CustomHostnamePointerSchema>;

/**
 * Hostname under .gitbook.io for a user or organization
 * /base-hostname/{domain}
 */
export const BaseHostnameSchema = HostnamePointerOrganizationSchema.extend({
    // Actual hostname string validation
    // Hostnames cannot start or end with an hyphen and must be between 3 and 32 characters long
    hostname: z
        .string()
        .regex(/^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/)
        .refine((hostname) => hostname !== 'www', {
            error: 'hostname cannot be www',
        })
        .refine((hostname) => hostname !== 'hosting', {
            error: 'hostname cannot be hosting',
        }),
    deprecated: z.boolean(),
});
export type BaseHostname = z.infer<typeof BaseHostnameSchema>;

/**
 * Status for a custom hostname during setup.
 */
export const CustomHostnameStatusSchema = z.union([
    z.enum(api.CustomHostnameDnsStatus),
    z.enum(api.CustomHostnameSslStatus),
    z.enum(api.CustomHostnameErrorStatus),
]);
export type CustomHostnameStatus = z.infer<typeof CustomHostnameStatusSchema>;

/**
 * Custom hostname pointing to an org.
 * /custom-hostname/{domain}
 */
export const CustomOrganizationHostnameSchema = HostnamePointerOrganizationSchema.extend({
    hostname: z.string(),
    deprecated: z.boolean(),
    status: CustomHostnameStatusSchema.optional(),
});
export type CustomOrganizationHostname = z.infer<typeof CustomOrganizationHostnameSchema>;

const CUSTOM_HOSTNAME_GITBOOK_DOMAINS_REGEX = /.*[.]gitbook[.](io|me)$/;
const CUSTOM_HOSTNAME_GITBOOKS_DOMAIN_REGEX = /.*[.]gitbooks[.]io$/;
const CUSTOM_HOSTNAME_GITHUB_DOMAIN_REGEX = /.*[.]github[.]io$/;
/**
 * Custom hostname pointing to any type of content.
 * /custom-hostname/{domain}
 */
export const CustomHostnameSchema = CustomHostnamePointerSchema.and(
    z.object({
        // Validate that it's a subdomain and doesn't match one of GitBook's zones
        hostname: z
            .string()
            .regex(/^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?[.]){2,}[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/)
            // GBN's CNAME for custom domains
            .refine((customHostname) => customHostname !== 'hosting.gitbook.com', {
                error: 'hostname cannot be hosting.gitbook.com',
            })
            // gitbook.io: Our own zone to host content
            // gitbook.me: GBN's legacy CDN, belongs to us
            .refine(
                (customHostname) => !CUSTOM_HOSTNAME_GITBOOK_DOMAINS_REGEX.test(customHostname),
                {
                    error: 'hostname cannot be a gitbook domain',
                }
            )
            // gitbooks.io: Legacy zone for public content
            .refine(
                (customHostname) => !CUSTOM_HOSTNAME_GITBOOKS_DOMAIN_REGEX.test(customHostname),
                {
                    error: 'hostname cannot be a gitbooks domain',
                }
            )
            // Too many users add a personal github.io domain, but this can't work...
            .refine((customHostname) => !CUSTOM_HOSTNAME_GITHUB_DOMAIN_REGEX.test(customHostname), {
                error: 'hostname cannot be a github domain',
            }),
        deprecated: z.boolean(),
        status: CustomHostnameStatusSchema.optional(),
    })
);
export type CustomHostname = z.infer<typeof CustomHostnameSchema>;

/**
 * Returns the expected CNAME for the given custom domain. This is the CNAME
 * we ask users to set on their custom domain to perform validation. It is based
 * on the key of the underlying owner, in order to prevent hijacking
 * deprecated custom domains.
 */
export function getExpectedCNAME(owner: HostnamePointerOrganization): string {
    let cname = `o${owner.organization}`;

    // hash the cname so that we don't have special characters, only take the first 10
    // so the cname doesn't come near subdomain limits
    cname = hash.sha256().update(cname).digest('hex').substring(0, 10);
    return `${cname}-${webConfig.content.cname}`.toLowerCase();
}
